/*
 * @providesModule SMSCodeInput
 */

import React, {useEffect, useRef} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {_m} from 'INTL';
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {getStyles} from 'SMSVerifyStyle';
import {formatCountdown} from '@skillz/ui';
import OtpInputs from 'react-native-otp-inputs';
import {SMSEvents} from 'SMSPhoneInput';
import SkillzStyle from 'SkillzStyle';
import SkillzSVG from 'SkillzSVG';
import {createStyles} from './SMSCodeInputStyle';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

export type SMSCodeInputProps = {
  //Value to allow user to press call instead
  callInsteadEnabled: {
    value: boolean;
    set: React.Dispatch<React.SetStateAction<boolean>>;
  };
  //Function to track aws events
  trackEvent: (name: string, params?: Object, metrics?: Object) => void;
  /** Function used to request verification code */
  callMeInsteadPress: () => void;
  //time for call instead
  time: number;
  //number of digits for code input
  numberOfInputs: number;
  //phone number for user
  phoneNumber: string;
  //country code for phone number
  countryValue: string;
  // function to be called on edit press for user
  editPress: () => void;
  // flag to disable editing of phone number
  disableEdit?: boolean;
  //function to set error message
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  //Value to show error styles or not
  errorStyle: {
    errorStyle: boolean;
    setErrorStyle: React.Dispatch<React.SetStateAction<boolean>>;
  };
  /** Function for validating verification code inputted by user */
  codeValidation: (
    code: string,
    phone: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void
  ) => void;
  // events used for trackEvent
  events: SMSEvents;
  phoneInUseLogin: boolean;
  colorVariant?: 'dark';
  isNewUI?: boolean;
};
const SMSCodeInputComponent = ({
  callInsteadEnabled,
  trackEvent,
  callMeInsteadPress,
  time,
  numberOfInputs,
  phoneNumber,
  countryValue,
  editPress,
  disableEdit,
  setErrorMessage,
  errorStyle,
  codeValidation,
  events,
  phoneInUseLogin,
  colorVariant,
  isNewUI,
}: SMSCodeInputProps) => {
  const {t} = useTranslation();
  const styles = getStyles(colorVariant);
  const newDefaultColor = SkillzStyle.getOverriddenFallbackTheme().cashIconGradient[0];
  const defaultColor = '#4890E8';
  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;

  const style = createStyles(isDarkTheme);
  const colorStyle =
    colorVariant === 'dark'
      ? {
          color: '#fff',
          ...SkillzStyle.fullStyles.fontExtraBold,
        }
      : {color: '#757575'};

  const {textColorPrimary, textColorTertiary, playButtonGradient} =
    SkillzStyle.getOverriddenFallbackTheme();
  const activeStyle = {
    ...(isNewUI ? styles.newCallTextStyle : styles.callTextStyle),
    color: isNewUI ? newDefaultColor : defaultColor,
    ...(isNewUI && {textDecorationColor: defaultColor}),
  };
  const inActiveStyle = {
    ...(isNewUI ? styles.newCallTextStyle : styles.callTextStyle),
    color: '#A7A7A7',
    ...(isNewUI && {textDecorationColor: '#A7A7A7'}),
  };
  const SECOND_TO_MS = 1000;
  const TEST_IDS = {
    callME: 'CallMe',
  };
  const countdown = callInsteadEnabled.value
    ? ''
    : ` in ${formatCountdown(t, time * SECOND_TO_MS)}`;

  const inputRef = useRef(null);
  const isValidatingRef = useRef(false);
  const lastValidatedCodeRef = useRef('');

  useEffect(() => {
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_IN_OTP_SCREEN_LOADED,
    });
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300); // slight delay ensures keyboard opens reliably
    return () => clearTimeout(timer);
  }, []);

  /* istanbul ignore next */
  const handleChange = (otpCode: string) => {
    // Only allow numeric characters and restrict length
    const numericOnly = otpCode.replace(/[^0-9]/g, '');
    const restrictedCode = numericOnly.slice(0, numberOfInputs);

    // If the processed code is different from input, return early
    if (restrictedCode !== otpCode) {
      return;
    }

    if (restrictedCode.length === numberOfInputs) {
      // Prevent multiple API calls for the same code or when already validating
      if (isValidatingRef.current || lastValidatedCodeRef.current === restrictedCode) {
        return;
      }

      // Mark as validating and store the code being validated
      isValidatingRef.current = true;
      lastValidatedCodeRef.current = restrictedCode;

      // Defer validation to after render completes to avoid React warning
      // and prevent state updates during render
      setTimeout(() => {
        trackEvent(events.codeEnter);
        const noSpecialCharacters = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');
        codeValidation(
          restrictedCode,
          `${countryValue}${noSpecialCharacters}`,
          () => {
            isValidatingRef.current = false;
          },
          (errorString) => {
            setErrorMessage(errorString);
            errorStyle.setErrorStyle(true);
            isValidatingRef.current = false;
            // Reset last validated code on error so user can retry
            lastValidatedCodeRef.current = '';
          }
        );
      }, 0);
    } else {
      // Only clear error style if not currently validating
      if (!isValidatingRef.current) {
        // Defer state update to after render completes
        setTimeout(() => {
          errorStyle.setErrorStyle(false);
        }, 0);
        // Reset last validated code when user is editing
        lastValidatedCodeRef.current = '';
      }
    }
  };

  if (isNewUI) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingContainer}
      >
        <View>
          {phoneInUseLogin && (
            <Text allowFontScaling={false} style={styles.phoneInUseHeader}>
              {_m("Your phone number appears to be in use, let's get you signed in:")}
            </Text>
          )}

          <Text
            allowFontScaling={false}
            style={[styles.newMessageHeader, isNewUI && {color: textColorPrimary.color}]}
          >
            {_m('Enter {0}-Digit Code', numberOfInputs)}
          </Text>
          <View style={styles.smsDescriptionContainer}>
            <Text
              allowFontScaling={false}
              style={[styles.smsDescriptionText, {color: textColorTertiary.color}]}
            >
              We sent you an SMS verification code to{'\n'}
              <Text
                allowFontScaling={false}
                onPress={disableEdit ? () => {} : editPress}
                style={[styles.phoneNumberText, {color: textColorPrimary.color}]}
              >
                {`${countryValue} ${phoneNumber}`}
                <SkillzSVG
                  tintColor={textColorPrimary.color}
                  fill='transparent'
                  source='edit_pencil.svg'
                  style={styles.editIcon}
                />
              </Text>
            </Text>
          </View>
          {errorStyle.errorStyle ? (
            <View style={styles.newCodeInputContainer}>
              <OtpInputs
                // @ts-ignore
                ref={inputRef}
                colorVariant={colorVariant}
                numberOfInputs={numberOfInputs}
                handleChange={handleChange}
                autofillFromClipboard={true}
                autofillListenerIntervalMS={2000}
                style={styles.newOtpStyle}
                filledStyles={styles.otpError}
                focusStyles={styles.otpError}
                inputStyles={style.otpInput}
                inputContainerStyles={[
                  isNewUI ? style.otpContainerWithBorder : styles.otpContainer,
                ]}
                errorStyle={errorStyle.errorStyle}
                allowFontScaling={false}
                inputMode='numeric'
              />
            </View>
          ) : (
            <View style={styles.newCodeInputContainer}>
              <OtpInputs
                // @ts-ignore
                ref={inputRef}
                colorVariant={colorVariant}
                autoFocus={true}
                numberOfInputs={numberOfInputs}
                handleChange={handleChange}
                autofillFromClipboard={true}
                autofillListenerIntervalMS={2000}
                style={styles.newOtpStyle}
                filledStyles={[style.otpSelected, {borderColor: playButtonGradient[0]}]}
                focusStyles={style.otpSelected}
                inputStyles={style.otpInput}
                inputContainerStyles={[
                  isNewUI ? style.otpContainerWithBorder : styles.otpContainer,
                ]}
                allowFontScaling={false}
                cursorColor={playButtonGradient[1]}
                inputMode='numeric'
              />
            </View>
          )}
          <Text
            allowFontScaling={false}
            onPress={() => {
              if (callInsteadEnabled.value) {
                callMeInsteadPress();
                trackEvent(events.callRequest);
              }
            }}
            style={[
              callInsteadEnabled.value ? activeStyle : inActiveStyle,
              {color: textColorPrimary.color, textDecorationColor: textColorPrimary.color},
            ]}
            testID={TEST_IDS.callME}
          >
            {_m('Call Me Instead')}
            {countdown}
          </Text>
        </View>
      </KeyboardAvoidingView>
    );
  }
  // @ts-ignore
  return (
    <View>
      {phoneInUseLogin && (
        <Text allowFontScaling={false} style={styles.phoneInUseHeader}>
          {_m("Your phone number appears to be in use, let's get you signed in:")}
        </Text>
      )}

      <Text allowFontScaling={false} style={styles.messageHeader}>
        {_m('Enter {0}-digit code', numberOfInputs)}
      </Text>
      <Trans
        t={_m}
        parent={Text}
        i18nKey={'SMS_CODE_INPUT_BODY'}
        components={{
          body: <Text allowFontScaling={false} style={styles.messageBody} />,
          phone: <Text allowFontScaling={false} style={[styles.messageBody, colorStyle]} />,
          edit: (
            <Text
              allowFontScaling={false}
              style={disableEdit ? styles.editButtonHidden : styles.editButton}
              onPress={disableEdit ? () => {} : editPress}
            />
          ),
        }}
        values={{phoneNumber: phoneNumber, countryCode: countryValue}}
      />
      {errorStyle.errorStyle ? (
        <View style={styles.codeInputContainer}>
          <OtpInputs
            // @ts-ignore
            colorVariant={colorVariant}
            numberOfInputs={numberOfInputs}
            handleChange={handleChange}
            autofillFromClipboard={true}
            autofillListenerIntervalMS={2000}
            style={styles.otpStyle}
            filledStyles={styles.otpError}
            focusStyles={styles.otpError}
            inputStyles={styles.otpInput}
            inputContainerStyles={styles.otpContainer}
            errorStyle={errorStyle.errorStyle}
          />
        </View>
      ) : (
        <View style={styles.codeInputContainer}>
          <OtpInputs
            // @ts-ignore
            colorVariant={colorVariant}
            autoFocus={true}
            numberOfInputs={numberOfInputs}
            handleChange={handleChange}
            autofillFromClipboard={true}
            autofillListenerIntervalMS={2000}
            style={styles.otpStyle}
            filledStyles={styles.otpSelected}
            focusStyles={styles.otpSelected}
            inputStyles={styles.otpInput}
            inputContainerStyles={styles.otpContainer}
            cursorColor={playButtonGradient[1]}
          />
        </View>
      )}
      <Text
        allowFontScaling={false}
        onPress={() => {
          if (callInsteadEnabled.value) {
            callMeInsteadPress();
            trackEvent(events.callRequest);
          }
        }}
        style={callInsteadEnabled.value ? activeStyle : inActiveStyle}
        testID={TEST_IDS.callME}
      >
        {_m('Call Me Instead')}
        {countdown}
      </Text>
    </View>
  );
};

export const SMSCodeInput = React.memo(SMSCodeInputComponent);
